import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

// Typing in a field must never trigger single-key shortcuts
const isTyping = (target) => {
  const tag = target?.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!target?.isContentEditable
}

/**
 * Single-key and two-key shortcuts, Jira/GitHub style: { c: fn, '?': fn, 'g b': fn }.
 * Ignored while typing or with Ctrl/Cmd/Alt held. A two-key shortcut waits 1 s for its
 * second key.
 */
export function useHotkeys(bindings, { enabled = true } = {}) {
  const ref = useRef(bindings)
  ref.current = bindings

  useEffect(() => {
    if (!enabled) return undefined
    let pending = null
    let timer = null

    const handler = (e) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return
      if (e.key.length !== 1) return
      const map = ref.current
      if (pending) {
        const combo = `${pending} ${e.key}`
        pending = null
        clearTimeout(timer)
        if (map[combo]) {
          e.preventDefault()
          map[combo](e)
        }
        return
      }
      if (Object.keys(map).some(k => k.startsWith(`${e.key} `))) {
        pending = e.key
        timer = setTimeout(() => { pending = null }, 1000)
        return
      }
      if (map[e.key]) {
        e.preventDefault()
        map[e.key](e)
      }
    }

    document.addEventListener('keydown', handler)
    return () => {
      document.removeEventListener('keydown', handler)
      clearTimeout(timer)
    }
  }, [enabled])
}

// App-wide Project Central shortcuts (the task page adds its own; see ShortcutHelpDialog)
export function useKeyboardShortcuts({ onSearch, onCreateTask, onShowHelp }) {
  const location = useLocation()
  const navigate = useNavigate()
  const isProjectCentral = location.pathname.startsWith('/projects')
  const projectId = location.pathname.match(/^\/projects\/(\d+)/)?.[1]

  useEffect(() => {
    if (!isProjectCentral) return

    const handler = (e) => {
      // Don't intercept when typing in inputs/textareas/contenteditable
      const tag = e.target.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) {
        if (e.key === 'Escape') {
          e.target.blur()
        }
        return
      }

      const isMeta = e.metaKey || e.ctrlKey

      // Cmd+K: focus search
      if (isMeta && e.key === 'k') {
        e.preventDefault()
        onSearch?.()
      }

      // Cmd+N: create task
      if (isMeta && e.key === 'n') {
        e.preventDefault()
        onCreateTask?.()
      }

      // Esc: navigate back from task page
      if (e.key === 'Escape') {
        const isTaskPage = /^\/projects\/\d+\/tasks\/\d+/.test(location.pathname)
        if (isTaskPage) {
          navigate(-1)
        }
      }
    }

    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isProjectCentral, location.pathname, navigate, onSearch, onCreateTask])

  useHotkeys({
    c: () => onCreateTask?.(),
    '/': () => onSearch?.(),
    '?': () => onShowHelp?.(),
    'g f': () => navigate('/projects/for-you'),
    'g i': () => navigate('/projects/issues'),
    'g p': () => navigate('/projects'),
    ...(projectId && {
      'g b': () => navigate(`/projects/${projectId}/board`),
      'g l': () => navigate(`/projects/${projectId}/list`)
    })
  }, { enabled: isProjectCentral })
}
